import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Download, Upload, Users, Search, ScanFace, Camera, X, Key, BadgeCheck, IdCard, RefreshCw, AlertTriangle, MailCheck, MailWarning, Clock4, UserCheck, UserX } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, Button, Field, Input, Select, Textarea, Modal, Badge } from "@/components/ui-kit";
import { usePagination } from "@/hooks/usePagination";
import { loadFaceApi, getCamera, stopStream, detectFace, snapshotVideo } from "@/lib/faceApi";
import { DatePicker } from "@/components/DatePicker";

/**
 * Karyawan — Iter v28 Phase 2.
 * Master data karyawan dengan import Excel, CRUD, search, dan pagination 25/page.
 * Foto profil & foto wajah akan diisi via halaman absensi (Phase 3) — di sini hanya teks data.
 */
const blank = {
  name: "", email: "", employee_code: "", nik: "", birth_date: "",
  phone: "", address: "", position: "", hire_date: "", status: "active",
  department_category: "",
  ktp_photo_b64: "",
  bank_name: "", bank_account_holder: "", bank_account_number: "",
  allow_debt: false,  // Iter v34d (#9) — Kasbon permission toggle (admin only)
  // Iter v34{ak} — Per-employee overtime override (null = inherit office defaults).
  overtime_enabled: null,
  max_overtime_hours: null,
  // Iter v34ay — Tarif overtime per jam (Rp). 0 = pakai default global.
  overtime_hourly_rate: 0,
  // Iter v34b2 — Gaji Pokok (bulanan)
  gaji_pokok: 0,
  // Iter v34b7 — Tarif per-karyawan tambahan
  gaji_harian: 0,           // Rp/hari kerja (karyawan harian)
  uang_makan_per_hari: 0,   // Rp/hari kerja (override komponen master "Uang Makan")
  ritase_rate: 0,           // Rp/rit (referensi untuk sopir/operator)
};

export default function Karyawan() {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("employees.edit");
  const canImport = hasPerm("employees.import");
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  // Iter v34 — filter tab by onboarding stage
  const [stageFilter, setStageFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [verifyEmp, setVerifyEmp] = useState(null);
  const [verifyPosition, setVerifyPosition] = useState("");
  const [ktpPreview, setKtpPreview] = useState(null);
  // Iter v28 Phase 3 — Admin face setup modal state
  const [faceModal, setFaceModal] = useState(null); // employee being captured for
  const [faceStream, setFaceStream] = useState(null);
  const [faceBusy, setFaceBusy] = useState(false);
  const [faceModalStatus, setFaceModalStatus] = useState("");
  const [faceModalError, setFaceModalError] = useState(null);
  const faceVideoRef = useRef(null);
  const faceCanvasRef = useRef(null);

  const refresh = () => api.get("/employees").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => {
    refresh();
    api.get("/department-categories").then((r) => setCategories(r.data)).catch(() => setCategories([]));
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    let list = rows;
    // Iter v34 — onboarding stage filter
    if (stageFilter !== "all") {
      list = list.filter((r) => {
        if (stageFilter === "email_unverified") return r.status === "registration_pending" && !r.email_verified;
        if (stageFilter === "profile_pending") return r.status === "registration_pending" && r.email_verified;
        if (stageFilter === "hr_pending") return r.status === "pending_verification";
        if (stageFilter === "active") return r.status === "active";
        if (stageFilter === "inactive") return r.status === "inactive" || r.status === "on_leave";
        return true;
      });
    }
    if (!s) return list;
    return list.filter((r) =>
      `${r.name} ${r.email} ${r.employee_code} ${r.nik} ${r.phone} ${r.position}`.toLowerCase().includes(s)
    );
  }, [rows, search, stageFilter]);

  // Iter v34 — Compute stage counts for tab badges
  const stageCounts = useMemo(() => ({
    all: rows.length,
    email_unverified: rows.filter((r) => r.status === "registration_pending" && !r.email_verified).length,
    profile_pending: rows.filter((r) => r.status === "registration_pending" && r.email_verified).length,
    hr_pending: rows.filter((r) => r.status === "pending_verification").length,
    active: rows.filter((r) => r.status === "active").length,
    inactive: rows.filter((r) => r.status === "inactive" || r.status === "on_leave").length,
  }), [rows]);

  const { pageItems, Pagination } = usePagination(filtered, { perPage: 25 });

  const openNew = () => { setForm(blank); setEditId(null); setOpen(true); };
  const openEdit = (e) => { setForm({ ...blank, ...e }); setEditId(e.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nama & email wajib diisi");
      return;
    }
    try {
      if (editId) {
        await api.patch(`/employees/${editId}`, form);
        toast.success("Data karyawan diperbarui");
      } else {
        await api.post("/employees", form);
        toast.success("Karyawan baru ditambahkan");
      }
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus karyawan ini?")) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Karyawan dihapus");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus");
    }
  };

  const downloadTemplate = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/import/template/employees`;
    window.open(url, "_blank");
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/import/employees", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Berhasil import ${r.data.inserted} karyawan. Dilewati: ${r.data.skipped_duplicates || 0}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal import");
    } finally {
      e.target.value = "";
    }
  };

  // Iter v28 Phase 3 — Provision login account for karyawan
  const provisionAccount = async (e) => {
    if (!window.confirm(`Buat akun login untuk ${e.name} dengan password default 'karyawan123'?`)) return;
    try {
      const r = await api.post(`/employees/${e.id}/provision-account`);
      toast.success(`Akun dibuat: ${r.data.email} / ${r.data.default_password}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal buat akun");
    }
  };

  // Iter v28 Phase 3 — Admin face setup flow (Iter v30: same-origin loader)
  const openFaceModal = async (emp) => {
    setFaceModal(emp); setFaceModalError(null); setFaceModalStatus("");
    try {
      await loadFaceApi((m) => setFaceModalStatus(m));
      const s = await getCamera(faceVideoRef.current, { facingMode: "user" });
      setFaceStream(s); setFaceModalStatus("");
    } catch (err) {
      setFaceModalError(err.message || "Gagal init kamera/model");
    }
  };

  const retryFaceModal = async () => {
    if (!faceModal) return;
    setFaceModalError(null);
    try {
      await loadFaceApi((m) => setFaceModalStatus(m));
      if (!faceStream) {
        const s = await getCamera(faceVideoRef.current, { facingMode: "user" });
        setFaceStream(s);
      }
      setFaceModalStatus("");
    } catch (err) { setFaceModalError(err.message || "Gagal retry"); }
  };

  const closeFaceModal = () => {
    stopStream(faceStream);
    setFaceStream(null); setFaceModal(null); setFaceBusy(false);
    setFaceModalStatus(""); setFaceModalError(null);
  };

  const captureAndSaveFace = async () => {
    if (!faceModal) return;
    setFaceBusy(true);
    try {
      const det = await detectFace(faceVideoRef.current);
      if (!det) throw new Error("Wajah tidak terdeteksi — coba lebih dekat, pencahayaan lebih baik");
      const selfie = snapshotVideo(faceVideoRef.current, faceCanvasRef.current, 0.6);
      await api.post(`/employees/${faceModal.id}/face`, {
        face_descriptor: det.descriptor, face_photo_b64: selfie,
      });
      toast.success(`Wajah ${faceModal.name} berhasil disimpan`);
      closeFaceModal();
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Gagal simpan wajah");
    } finally {
      setFaceBusy(false);
    }
  };

  const resetFace = async (emp) => {
    if (!window.confirm(`Hapus wajah terdaftar untuk ${emp.name}? Karyawan harus daftar ulang.`)) return;
    try {
      await api.delete(`/employees/${emp.id}/face`);
      toast.success("Wajah direset");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal reset");
    }
  };

  // Iter v34 — Admin override email verification for stuck karyawan
  const markEmailVerified = async (emp) => {
    if (!window.confirm(`Tandai email ${emp.email} sebagai terverifikasi (override)?`)) return;
    try {
      // Lookup user id by email
      const users = await api.get("/users").then((r) => r.data);
      const u = users.find((x) => (x.email || "").toLowerCase() === (emp.email || "").toLowerCase());
      if (!u) return toast.error("Akun user belum dibuat untuk karyawan ini");
      await api.post(`/users/${u.id}/mark-email-verified`);
      toast.success("Email berhasil ditandai terverifikasi");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal override verifikasi");
    }
  };

  // Iter v29 — Verify pending karyawan (assign position + activate)
  const openVerify = (emp) => { setVerifyEmp(emp); setVerifyPosition(emp.position || ""); };
  const submitVerify = async () => {
    if (!verifyPosition.trim()) return toast.error("Jabatan wajib diisi");
    try {
      await api.post(`/employees/${verifyEmp.id}/verify`, { position: verifyPosition.trim() });
      toast.success(`${verifyEmp.name} berhasil diverifikasi`);
      setVerifyEmp(null); setVerifyPosition("");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal verifikasi");
    }
  };

  return (
    <div className="space-y-4" data-testid="karyawan-page">
      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-terra-600" />
            <h3 className="font-display text-lg font-semibold text-stone-900">Data Karyawan</h3>
            <Badge tone="stone">{rows.length} total</Badge>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-stone-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari nama / email / NIK..."
                className="inp pl-8 !text-sm w-64"
                data-testid="karyawan-search"
              />
            </div>
            {canImport && (
              <>
                <Button variant="outline" onClick={downloadTemplate} data-testid="karyawan-template-btn">
                  <Download className="h-3.5 w-3.5" /> Template
                </Button>
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider border border-stone-300 hover:bg-stone-50 cursor-pointer" data-testid="karyawan-import-label">
                  <Upload className="h-3.5 w-3.5" /> Import Excel
                  <input type="file" accept=".xlsx,.xls" onChange={importExcel} className="hidden" data-testid="karyawan-import-input" />
                </label>
              </>
            )}
            {canEdit && (
              <Button onClick={openNew} data-testid="karyawan-add-btn">
                <Plus className="h-3.5 w-3.5" /> Tambah
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Iter v34 — Onboarding stage filter tabs */}
      <Card className="p-0 overflow-x-auto" data-testid="karyawan-stage-tabs">
        <div className="flex items-stretch border-b border-stone-200 text-xs min-w-max">
          {[
            { k: "all", label: "Semua", icon: Users, tone: "stone" },
            { k: "email_unverified", label: "Belum Verif Email", icon: MailWarning, tone: "amber" },
            { k: "profile_pending", label: "Belum Lengkapi Profil", icon: Clock4, tone: "blue" },
            { k: "hr_pending", label: "Menunggu Approval HR", icon: BadgeCheck, tone: "amber" },
            { k: "active", label: "Aktif", icon: UserCheck, tone: "emerald" },
            { k: "inactive", label: "Non-Aktif", icon: UserX, tone: "stone" },
          ].map((t) => {
            const Icon = t.icon;
            const active = stageFilter === t.k;
            const count = stageCounts[t.k] || 0;
            return (
              <button
                key={t.k}
                onClick={() => setStageFilter(t.k)}
                className={`flex-shrink-0 px-4 py-3 inline-flex items-center gap-2 border-b-2 transition-colors ${active ? "border-terra-500 text-terra-700 bg-terra-50/30" : "border-transparent text-stone-600 hover:bg-stone-50"}`}
                data-testid={`karyawan-tab-${t.k}`}
              >
                <Icon className="h-3.5 w-3.5" />
                <span className="font-medium">{t.label}</span>
                <Badge tone={active ? "terra" : "stone"} className="!text-[10px] !px-1.5 !py-0">{count}</Badge>
              </button>
            );
          })}
        </div>
      </Card>

      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
            <tr>
              <th className="text-left px-3 py-3">Kode</th>
              <th className="text-left px-3 py-3">Nama</th>
              <th className="text-left px-3 py-3">Email</th>
              <th className="text-left px-3 py-3">Posisi</th>
              <th className="text-left px-3 py-3">NIK</th>
              <th className="text-left px-3 py-3">No HP</th>
              <th className="text-center px-3 py-3">Tahap Onboarding</th>
              <th className="text-center px-3 py-3">Wajah</th>
              {canEdit && <th className="px-3 py-3"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {pageItems.length === 0 && (
              <tr><td colSpan={canEdit ? 9 : 8} className="px-3 py-12 text-center text-xs text-stone-400">Belum ada data karyawan.</td></tr>
            )}
            {pageItems.map((e) => (
              <tr key={e.id} className="hover:bg-stone-50/40" data-testid={`karyawan-row-${e.id}`}>
                <td className="px-3 py-2.5 font-mono text-xs text-stone-700">{e.employee_code || "—"}</td>
                <td className="px-3 py-2.5 font-medium text-stone-900">{e.name}</td>
                <td className="px-3 py-2.5 text-stone-600 text-xs">{e.email}</td>
                <td className="px-3 py-2.5 text-stone-700">{e.position || "—"}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-stone-600">{e.nik || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-stone-600">{e.phone || "—"}</td>
                <td className="px-3 py-2.5 text-center">
                  {/* Iter v34 — Onboarding stage badge */}
                  {(() => {
                    if (e.status === "registration_pending" && !e.email_verified) {
                      return <Badge tone="amber" data-testid={`karyawan-stage-${e.id}`}><MailWarning className="h-3 w-3 inline mr-1" />Verif Email</Badge>;
                    }
                    if (e.status === "registration_pending" && e.email_verified) {
                      return <Badge tone="blue" data-testid={`karyawan-stage-${e.id}`}><Clock4 className="h-3 w-3 inline mr-1" />Lengkapi Profil</Badge>;
                    }
                    if (e.status === "pending_verification") {
                      return <Badge tone="amber" data-testid={`karyawan-stage-${e.id}`}><BadgeCheck className="h-3 w-3 inline mr-1" />Approval HR</Badge>;
                    }
                    if (e.status === "active") {
                      return <Badge tone="emerald" data-testid={`karyawan-stage-${e.id}`}><UserCheck className="h-3 w-3 inline mr-1" />Aktif</Badge>;
                    }
                    if (e.status === "on_leave") {
                      return <Badge tone="amber" data-testid={`karyawan-stage-${e.id}`}>Cuti</Badge>;
                    }
                    return <Badge tone="stone" data-testid={`karyawan-stage-${e.id}`}><UserX className="h-3 w-3 inline mr-1" />Non-Aktif</Badge>;
                  })()}
                </td>
                <td className="px-3 py-2.5 text-center">
                  {e.face_descriptor && e.face_descriptor.length === 128 ? (
                    <Badge tone="emerald" data-testid={`karyawan-face-ok-${e.id}`}>✓ Terdaftar</Badge>
                  ) : (
                    <Badge tone="stone">— Belum</Badge>
                  )}
                </td>
                {canEdit && (
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {e.status === "registration_pending" && !e.email_verified && (
                      <button onClick={() => markEmailVerified(e)} className="text-blue-600 hover:text-blue-800 px-2" title="Override Verifikasi Email" data-testid={`karyawan-mark-email-${e.id}`}>
                        <MailCheck className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {e.status === "pending_verification" && (
                      <button onClick={() => openVerify(e)} className="text-amber-600 hover:text-amber-800 px-2" title="Verifikasi & Aktifkan" data-testid={`karyawan-verify-${e.id}`}>
                        <BadgeCheck className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {e.ktp_photo_b64 && (
                      <button onClick={() => setKtpPreview(e)} className="text-stone-500 hover:text-blue-700 px-2" title="Lihat KTP" data-testid={`karyawan-ktp-${e.id}`}>
                        <IdCard className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button onClick={() => openFaceModal(e)} className="text-stone-500 hover:text-emerald-700 px-2" title="Setup Wajah" data-testid={`karyawan-face-${e.id}`}>
                      <ScanFace className="h-3.5 w-3.5" />
                    </button>
                    {e.face_descriptor && (
                      <button onClick={() => resetFace(e)} className="text-stone-500 hover:text-amber-700 px-2" title="Reset Wajah" data-testid={`karyawan-face-reset-${e.id}`}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button onClick={() => provisionAccount(e)} className="text-stone-500 hover:text-blue-700 px-2" title="Buat Akun Login" data-testid={`karyawan-prov-${e.id}`}>
                      <Key className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => openEdit(e)} className="text-stone-500 hover:text-terra-700 px-2" title="Edit" data-testid={`karyawan-edit-${e.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => remove(e.id)} className="text-stone-500 hover:text-red-700 px-2" title="Hapus" data-testid={`karyawan-del-${e.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {Pagination}
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Karyawan" : "Tambah Karyawan"} size="lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Nama Lengkap" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="karyawan-form-name" />
          </Field>
          <Field label="Email" required hint="Untuk login portal karyawan (Phase 3)">
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="karyawan-form-email" />
          </Field>
          <Field label="Kode Karyawan">
            <Input value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} placeholder="EMP-001" />
          </Field>
          <Field label="NIK">
            <Input value={form.nik} onChange={(e) => setForm({ ...form, nik: e.target.value })} placeholder="16 digit" />
          </Field>
          <Field label="Tanggal Lahir">
            <DatePicker value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
          </Field>
          <Field label="No HP">
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="08xxxxxxxxx" />
          </Field>
          <Field label="Posisi / Jabatan">
            <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
          </Field>
          <Field label="Kategori Departemen">
            <Select value={form.department_category} onChange={(e) => setForm({ ...form, department_category: e.target.value })} data-testid="karyawan-form-category">
              <option value="">— Pilih —</option>
              {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Tanggal Masuk">
            <DatePicker value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} />
          </Field>
          <Field label="Status">
            <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="active">Aktif</option>
              <option value="registration_pending">Registrasi (belum lengkap)</option>
              <option value="pending_verification">Menunggu Approval HR</option>
              <option value="on_leave">Cuti</option>
              <option value="inactive">Non-Aktif</option>
            </Select>
          </Field>
          <Field label="Alamat" className="md:col-span-2">
            <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </Field>
          <Field label="Nama Bank">
            <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="contoh: BCA, Mandiri" data-testid="karyawan-form-bank-name" />
          </Field>
          <Field label="Nama Pemilik Rekening">
            <Input value={form.bank_account_holder} onChange={(e) => setForm({ ...form, bank_account_holder: e.target.value })} data-testid="karyawan-form-bank-holder" />
          </Field>
          <Field label="Nomor Rekening" className="md:col-span-2">
            <Input value={form.bank_account_number} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} data-testid="karyawan-form-bank-number" />
          </Field>
          {/* Iter v34d (#9) — Kasbon permission toggle. Default OFF. Admin enables per trusted karyawan. */}
          <Field label="Izin Kasbon (Hutang)" className="md:col-span-2">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.allow_debt}
                onChange={(e) => setForm({ ...form, allow_debt: e.target.checked })}
                className="h-4 w-4 accent-terra-500"
                data-testid="karyawan-form-allow-debt"
              />
              <span className="text-xs text-stone-700">
                Izinkan karyawan ini melakukan kasbon (pinjam uang). Saat aktif, akan muncul menu <b>Kasbon Saya</b> di portal karyawan untuk melihat riwayat &amp; saldo hutangnya.
              </span>
            </label>
          </Field>
          {/* Iter v34{ak} — Per-employee overtime override. */}
          <Field label="Lembur (Overtime)" className="md:col-span-2">
            <div className="space-y-2">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.overtime_enabled !== false}
                  onChange={(e) => setForm({ ...form, overtime_enabled: e.target.checked })}
                  className="h-4 w-4 accent-terra-500"
                  data-testid="karyawan-form-overtime-enabled"
                />
                <span className="text-xs text-stone-700">
                  <b>Aktifkan lembur</b> untuk karyawan ini. Uncheck untuk menonaktifkan (jam kerja di atas 8 jam tidak dihitung sebagai lembur).
                </span>
              </label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-stone-600 whitespace-nowrap">Batas maksimal (jam/hari):</span>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="12"
                  value={form.max_overtime_hours ?? ""}
                  onChange={(e) => setForm({ ...form, max_overtime_hours: e.target.value === "" ? null : Number(e.target.value) })}
                  placeholder="Ikut default kantor"
                  className="!w-40"
                  data-testid="karyawan-form-max-overtime"
                />
                <span className="text-[11px] text-stone-400">Kosongkan = ikut default kantor</span>
              </div>
              {/* Iter v34ay — Tarif overtime per jam */}
              <div className="flex items-center gap-2 pt-2 border-t border-stone-100">
                <span className="text-xs text-stone-600 whitespace-nowrap">Tarif per jam (Rp):</span>
                <Input
                  type="number"
                  step="1000"
                  min="0"
                  value={form.overtime_hourly_rate ?? 0}
                  onChange={(e) => setForm({ ...form, overtime_hourly_rate: e.target.value === "" ? 0 : Number(e.target.value) })}
                  placeholder="0 = pakai default global"
                  className="!w-48 font-mono-num"
                  data-testid="karyawan-form-overtime-rate"
                />
                <span className="text-[11px] text-stone-400">0 / kosong = pakai default global (di Pengaturan)</span>
              </div>
              {/* Iter v34b2 — Gaji Pokok (untuk Payroll) */}
              <div className="flex items-center gap-2 pt-2 border-t border-stone-100">
                <span className="text-xs text-stone-600 whitespace-nowrap">Gaji Pokok (Rp/bulan):</span>
                <Input
                  type="number"
                  step="10000"
                  min="0"
                  value={form.gaji_pokok ?? 0}
                  onChange={(e) => setForm({ ...form, gaji_pokok: e.target.value === "" ? 0 : Number(e.target.value) })}
                  placeholder="mis: 4000000"
                  className="!w-48 font-mono-num"
                  data-testid="karyawan-form-gaji-pokok"
                />
                <span className="text-[11px] text-stone-400">Basis Payroll (bulanan)</span>
              </div>
              {/* Iter v34b7 — Tarif per-karyawan */}
              <div className="flex items-center gap-2 pt-2 border-t border-stone-100">
                <span className="text-xs text-stone-600 whitespace-nowrap">Gaji Harian (Rp/hari):</span>
                <Input
                  type="number"
                  step="1000"
                  min="0"
                  value={form.gaji_harian ?? 0}
                  onChange={(e) => setForm({ ...form, gaji_harian: e.target.value === "" ? 0 : Number(e.target.value) })}
                  placeholder="0 = pakai gaji pokok"
                  className="!w-48 font-mono-num"
                  data-testid="karyawan-form-gaji-harian"
                />
                <span className="text-[11px] text-stone-400">Karyawan harian — bila diisi, dipakai sebagai basis Payroll (× hari hadir), menggantikan Gaji Pokok</span>
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-stone-100">
                <span className="text-xs text-stone-600 whitespace-nowrap">Uang Makan (Rp/hari):</span>
                <Input
                  type="number"
                  step="1000"
                  min="0"
                  value={form.uang_makan_per_hari ?? 0}
                  onChange={(e) => setForm({ ...form, uang_makan_per_hari: e.target.value === "" ? 0 : Number(e.target.value) })}
                  placeholder="0 = pakai default master"
                  className="!w-48 font-mono-num"
                  data-testid="karyawan-form-uang-makan"
                />
                <span className="text-[11px] text-stone-400">Override komponen &quot;Uang Makan&quot; per-karyawan</span>
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-stone-100">
                <span className="text-xs text-stone-600 whitespace-nowrap">Ritase (Rp/rit):</span>
                <Input
                  type="number"
                  step="1000"
                  min="0"
                  value={form.ritase_rate ?? 0}
                  onChange={(e) => setForm({ ...form, ritase_rate: e.target.value === "" ? 0 : Number(e.target.value) })}
                  placeholder="0"
                  className="!w-48 font-mono-num"
                  data-testid="karyawan-form-ritase-rate"
                />
                <span className="text-[11px] text-stone-400">Tarif per rit (sopir/operator) — referensi</span>
              </div>
            </div>
          </Field>
          {form.ktp_photo_b64 && (
            <div className="md:col-span-2">
              <p className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Foto KTP Terdaftar</p>
              <img src={`data:image/jpeg;base64,${form.ktp_photo_b64}`} alt="KTP" className="max-h-40 border border-stone-200" />
            </div>
          )}
          <div className="md:col-span-2 text-[11px] text-stone-500 bg-stone-50 border border-stone-200 p-3">
            📷 Foto wajah untuk verifikasi absensi via tombol <b>Setup Wajah</b> di daftar karyawan. KTP diunggah saat karyawan mendaftar mandiri.
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-stone-200">
          <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
          <Button onClick={save} data-testid="karyawan-save-btn">{editId ? "Simpan Perubahan" : "Tambah Karyawan"}</Button>
        </div>
      </Modal>

      {/* Iter v28 Phase 3 — Admin face setup modal */}
      <Modal open={!!faceModal} onClose={closeFaceModal} title={`Setup Wajah · ${faceModal?.name || ""}`} size="md">
        <div className="space-y-3" data-testid="karyawan-face-modal">
          <p className="text-xs text-stone-600">Nyalakan kamera, pastikan wajah karyawan terlihat jelas di tengah frame, lalu klik &ldquo;Simpan Wajah&rdquo;. Threshold ketat (0.40) — wajah harus terdaftar dengan baik.</p>

          {(faceModalError || faceModalStatus) && (
            <div className={`p-3 text-xs border ${faceModalError ? "bg-red-50 border-red-200" : "bg-blue-50 border-blue-200"}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className={faceModalError ? "text-red-800 font-medium" : "text-blue-800"}>
                  {faceModalError ? `Gagal: ${faceModalError}` : faceModalStatus}
                </span>
                {faceModalError && (
                  <Button onClick={retryFaceModal} className="!bg-blue-600 hover:!bg-blue-700 !text-xs" data-testid="karyawan-face-retry">
                    <RefreshCw className="h-3.5 w-3.5" /> Coba lagi
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="relative bg-stone-900 aspect-video overflow-hidden">
            <video ref={faceVideoRef} className="w-full h-full object-cover" muted autoPlay playsInline data-testid="karyawan-face-video" />
            <canvas ref={faceCanvasRef} className="hidden" />
            {!faceStream && !faceModalError && (
              <div className="absolute inset-0 flex items-center justify-center text-stone-300 text-xs">Memuat kamera…</div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={closeFaceModal}>Batal</Button>
            <Button onClick={captureAndSaveFace} disabled={faceBusy || !faceStream || !!faceModalError} data-testid="karyawan-face-save">
              <Camera className="h-3.5 w-3.5" /> {faceBusy ? "Memproses…" : "Simpan Wajah"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Iter v29 — Verify pending karyawan modal */}
      <Modal open={!!verifyEmp} onClose={() => setVerifyEmp(null)} title={`Verifikasi Karyawan · ${verifyEmp?.name || ""}`} size="md">
        {verifyEmp && (
          <div className="space-y-4" data-testid="karyawan-verify-modal">
            <div className="bg-amber-50 border border-amber-200 p-3 text-xs space-y-1">
              <div><b>Email:</b> {verifyEmp.email}</div>
              <div><b>NIK:</b> {verifyEmp.nik || "—"}</div>
              <div><b>Departemen:</b> {verifyEmp.department_category || "—"}</div>
              <div><b>No HP:</b> {verifyEmp.phone || "—"}</div>
              <div><b>Bank:</b> {verifyEmp.bank_name} a.n. {verifyEmp.bank_account_holder} ({verifyEmp.bank_account_number})</div>
            </div>
            {verifyEmp.ktp_photo_b64 && (
              <div>
                <p className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Foto KTP</p>
                <img src={`data:image/jpeg;base64,${verifyEmp.ktp_photo_b64}`} alt="KTP" className="max-h-48 border border-stone-200" />
              </div>
            )}
            <Field label="Jabatan / Posisi *" hint="Wajib diisi untuk mengaktifkan akun ini">
              <Input value={verifyPosition} onChange={(e) => setVerifyPosition(e.target.value)} placeholder="contoh: Operator Batching, Sopir, Admin Proyek" data-testid="karyawan-verify-position" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setVerifyEmp(null)}>Batal</Button>
              <Button onClick={submitVerify} data-testid="karyawan-verify-confirm">
                <BadgeCheck className="h-3.5 w-3.5" /> Aktifkan Akun
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Iter v29 — KTP preview modal */}
      <Modal open={!!ktpPreview} onClose={() => setKtpPreview(null)} title={`Foto KTP · ${ktpPreview?.name || ""}`} size="lg">
        {ktpPreview?.ktp_photo_b64 && (
          <div className="space-y-3" data-testid="karyawan-ktp-modal">
            <img src={`data:image/jpeg;base64,${ktpPreview.ktp_photo_b64}`} alt="KTP" className="max-w-full max-h-[70vh] mx-auto border border-stone-200" />
            <div className="text-xs text-stone-600 grid grid-cols-2 gap-2 bg-stone-50 p-3 border border-stone-200">
              <div><b>Nama:</b> {ktpPreview.name}</div>
              <div><b>NIK:</b> {ktpPreview.nik || "—"}</div>
              <div><b>Lahir:</b> {ktpPreview.birth_date || "—"}</div>
              <div><b>No HP:</b> {ktpPreview.phone || "—"}</div>
              <div className="col-span-2"><b>Alamat:</b> {ktpPreview.address || "—"}</div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
